import { getBackendUrl } from "../../../utils";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ButtonComponent from "../../../cubeComponent/form/ButtonComponent";
import { BUTTON_TYPES } from "../../../cubeComponent/constants/globalVariable";
import axiosWrapper from "../../../services/axiosWrapper";
import { CheckBoxComponent } from "@syncfusion/ej2-react-buttons";
import { showToastNotification } from "../../../actions/toast";
import { TOAST_TYPE_ERROR } from "../../../services/constants";
import _ from "lodash";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";

interface FileUploadProps {
  selectedFiles?: any;
  onCloseModal?: any;
  path?: any;
  onDeleteSuccess?: any;
  workspaceId?: any;
  isFileUpload?: any;
  onDeleteFiles?: any;
}
function FileUpload({
  selectedFiles,
  onCloseModal,
  path,
  onDeleteSuccess,
  workspaceId,
  isFileUpload = false,
  onDeleteFiles,
}: Readonly<FileUploadProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  let selectedResource = "file";
  const resourceType = _.get(selectedFiles, "[0].resourceType");
  if (resourceType == "RESOURCE_TYPE_SCHEDULE") {
    selectedResource = "schedule";
  } else if (resourceType == "RESOURCE_TYPE_ESTIMATE") {
    selectedResource = "estimate";
  } else if (resourceType == "RESOURCE_TYPE_CONTRACT") {
    selectedResource = "contract";
  }

  const checkIfFoldersAndFiles = { folder: false, file: false };

  if (selectedFiles.length === 1) {
    if (resourceType == "RESOURCE_TYPE_FOLDER") {
      selectedResource = "folder";
    }
  } else {
    selectedFiles.forEach(({ resourceType }: { resourceType: any }) => {
      if (resourceType == "RESOURCE_TYPE_FOLDER") {
        checkIfFoldersAndFiles.folder = true;
      } else {
        checkIfFoldersAndFiles.file = true;
      }
    });

    if (checkIfFoldersAndFiles.file && checkIfFoldersAndFiles.folder) {
      selectedResource = "items";
    } else if (!checkIfFoldersAndFiles.file && checkIfFoldersAndFiles.folder) {
      selectedResource = "folders";
    } else {
      selectedResource = "files";
    }
  }

  const [constituentFilesMap, setConstituentFilesMap] = useState<any>({});
  const [showConstituentWarning, setShowConstituentWarning] = useState<any>(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [deleteAssociatedTasks, setDeleteAssociatedTasks] = useState(true);
  const [deleteWarningText, setDeleteWarningText] = useState(
    `Are you sure you want to delete ${selectedFiles.length === 1 ? "this" : "these"} ${
      selectedFiles.length > 1 ? selectedFiles.length : ""
    } ${selectedResource}?`,
  );
  const isSchedule = resourceType == "RESOURCE_TYPE_SCHEDULE";
  const isEstimate = resourceType == "RESOURCE_TYPE_ESTIMATE";
  useEffect(() => {
    if (Object.values(constituentFilesMap).some((value: any) => value.length > 0)) {
      setShowConstituentWarning(true);
      setShowDeleteWarning(false);
    } else {
      setShowDeleteWarning(true);
      if (isSchedule) {
        setDeleteWarningText("All constituent schedules will also get deleted. Are you sure you want to delete?");
      }
    }
  }, [constituentFilesMap]);

  useEffect(() => {
    if (!isFileUpload) {
      checkIfFileIsConstituentFile();
    }
  }, []);

  const checkIfFileIsConstituentFile = async () => {
    if (isEstimate || isSchedule) {
      return;
    }
    const fileId = selectedFiles.map((file: any) => file.hierarchyId);
    const data = {
      fileId,
    };
    const response = await axiosWrapper.post(`${getBackendUrl()}/fileUpload/getParentOfConstituentFiles`, data);
    setConstituentFilesMap(response.data.data || {});
  };

  const handleDeleteFileButton = async () => {
    if (onDeleteFiles) {
      onDeleteFiles();
    }
    const selectedFilesToDelete: any = [];
    selectedFiles.map((eachObj: any) => {
      const data = {
        ...eachObj,
        spaceId: workspaceId,
        deleteAssociatedTasks,
      };
      if (!isFileUpload || eachObj.hierarchyId) {
        selectedFilesToDelete.push(data);
      }
    });
    const data = {
      action: "delete",
      data: selectedFilesToDelete,
      path,
    };
    const response = await axiosWrapper.post(`${getBackendUrl()}/fileSystemServer`, data);
    if (response.data.status) {
      onDeleteSuccess(true);
    } else {
      onDeleteSuccess(false);
      dispatch(
        showToastNotification({
          content: response.data.message,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleCancelDeleteFileButton = () => {
    onCloseModal();
  };

  const footerContent = () => (
    <div className="flex items-center gap-6">
      <ButtonComponent onClick={handleCancelDeleteFileButton} buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}>
        {t("Cancel")}
      </ButtonComponent>

      <ButtonComponent onClick={handleDeleteFileButton} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
        {t("Confirm")}
      </ButtonComponent>
    </div>
  );
  const modelContent = () => (
    <div>
      {showDeleteWarning && <div>{deleteWarningText}</div>}
      {showConstituentWarning && (
        <div className="flex flex-col gap-4">
          <div>{t("Below files are the constituent files of")}</div>
          <div>
            {Object.keys(constituentFilesMap).map((fileId: any) => {
              if (constituentFilesMap[fileId].length > 0) {
                let fileName = selectedFiles.filter((file: any) => file.hierarchyId == fileId);
                const parentFileNames = constituentFilesMap[fileId].map((file: any) => file.name).join(", ");
                fileName = fileName[0].name;

                return (
                  <div key={fileId} className="flex">
                    <div>{fileName}</div>
                    <div className="ps-4 font-semibold">{parentFileNames}</div>
                  </div>
                );
              }
            })}
          </div>
          <div>{t("Are you sure you want to delete?")}</div>
        </div>
      )}
      {isSchedule && (
        <span>
          <CheckBoxComponent
            label="Delete associated tasks"
            id="deleteTask"
            checked={deleteAssociatedTasks}
            onChange={(args: any) => {
              setDeleteAssociatedTasks(args.target.checked);
            }}
          />{" "}
        </span>
      )}
    </div>
  );

  return (
    <ModalStateDialogComponent
      onCancel={handleCancelDeleteFileButton}
      headerTitle={`Delete ${selectedResource}`}
      footerContent={footerContent()}
      modelContent={modelContent()}
      width="50%"
      isVisible
    />
  );
}

export default FileUpload;
